import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  deployBlog,
  uploadImage,
  updatePost,
  usePostById,
  type NewPost,
  uploadImgByUrl,
  useBlogMeta,
  deletePost,
  uploadAndNormalizeUrl
} from "./api";
import { CheckCircle2Icon, ImagePlusIcon, PlusIcon, X } from "lucide-react";
import { debounce, toTranslit } from "./utils";
import { baseEditorConfig } from "./editor";
import EditorJS from "@editorjs/editorjs";
import { useBlogStore } from "./store";
import { AddCategoryModal } from "./components/AddCategoryModal";
import { Button } from "./components/Button";
import { Selector } from "./components/Selector";
// @ts-ignore
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { DeletePostModal } from "./components/DeletePostModal";
import { CreateLocalizationModal } from "./components/CreateLocalizationModal";
import { availableLanguages } from "../shared";
import { Controller, useForm, useFormState, useWatch } from "react-hook-form";

export function EditPost() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedBlog } = useBlogStore();

  const form = useForm<NewPost>();
  const { control, register, reset, setValue, getValues, handleSubmit } = form;

  const { data: post, loading: postLoading } = usePostById(id!, { onSuccess: (data) => reset(data) });
  const { data: meta = { authors: [], categories: [] }, refetch: refetchMeta } = useBlogMeta(selectedBlog);

  const editorRef = useRef<EditorJS | null>(null);

  const [tab, setTab] = useState<"content" | "meta">("content");
  const [action, setAction] = useState<"saving" | "publishing" | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [previewImageFile, setPreviewImageFile] = useState<File | null>(null);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLocalizationModalOpen, setIsLocalizationModalOpen] = useState(false);

  const formValues = useWatch({ control });

  const { isDirty, dirtyFields } = useFormState({ control });

  const handleCategoryAdded = async (newCategory: { slug: string; title: string }) => {
    setValue("category", newCategory.slug, { shouldDirty: true });
    refetchMeta?.();
  };

  // Only infer the slug from the title while the slug is still empty, so a published one is never overwritten
  const inferSlugFromTitle = (title: string) => {
    if (getValues("slug")?.trim()) return;
    setValue("slug", toTranslit(title.trim()), { shouldDirty: true });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const { files } = e.dataTransfer;

    // Native file drop
    if (files && files.length && files[0]?.type.startsWith("image/")) {
      setPreviewImageFile(files[0]);
      setValue("previewImage", URL.createObjectURL(files[0]), { shouldDirty: true });
      return;
    }

    // URL drop
    const url = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
    if (!url) return;

    setValue("previewImage", url);
  };

  const onSubmit = async (data: Partial<NewPost>, action: "edit" | "publish" | "edit_and_publish") => {
    if (!selectedBlog || !editorRef.current) return;

    setAction(action === "edit" ? "saving" : "publishing");

    const { blocks } = await editorRef.current.save();

    const updatedBlocks = await Promise.all(
      blocks.map(async (block) => {
        // image block
        if (block.type === "image") {
          return {
            ...block,
            data: {
              ...block.data,
              file: {
                ...block.data.file,
                url: await uploadAndNormalizeUrl(block.data.file.url)
              }
            }
          };
        }

        // banner block
        if (block.type === "banner" && block.data.image) {
          return {
            ...block,
            data: {
              ...block.data,
              image: { ...block.data.image, url: await uploadAndNormalizeUrl(block.data.image.url) }
            }
          };
        }

        return block;
      })
    );

    let finalPreviewImage = data.previewImage;

    if (previewImageFile) {
      const uploadRes = await uploadImage(previewImageFile);
      if (uploadRes.error) {
        console.error("Image upload failed:", uploadRes.error);
        setAction(null);

        return;
      }
      finalPreviewImage = `${window.location.origin}/${uploadRes.data?.path}`;
    }

    if (!previewImageFile && data.previewImage && !data.previewImage.startsWith(window.location.origin)) {
      const uploadRes = await uploadImgByUrl(data.previewImage);
      if (uploadRes.error) {
        console.error("Image upload failed:", uploadRes.error);
        setAction(null);

        return;
      }
      finalPreviewImage = `${window.location.origin}/${uploadRes.data?.path}`;
    }

    if (action === "edit_and_publish" || action === "edit") {
      // Only include fields that have been modified
      const changedFields: Partial<NewPost> = {};

      for (const key of Object.keys(dirtyFields) as Array<keyof NewPost>) {
        if (key in data) {
          (changedFields as Record<string, unknown>)[key] = data[key];
        }
      }

      // Always include content if it was changed
      if (dirtyFields.content) {
        changedFields.content = updatedBlocks;
      }

      // Handle previewImage separately since it may have been processed
      if (dirtyFields.previewImage || previewImageFile) {
        changedFields.previewImage = finalPreviewImage;
      }

      await updatePost(id!, changedFields);
      reset({ ...data, content: updatedBlocks, previewImage: finalPreviewImage });
    }

    if (action === "edit_and_publish" || action === "publish") await deployBlog(id!);

    setMessage(action === "edit" ? "Draft updated successfully!" : "Post published successfully!");
    setTimeout(() => setMessage(null), 1500);

    setPreviewImageFile(null);
    setAction(null);
  };

  useEffect(() => {
    if (!document.getElementById("editorjs") || postLoading) return;

    editorRef.current = new EditorJS({
      ...baseEditorConfig,
      data: { blocks: formValues.content || [] },

      onChange: debounce(async (api) => {
        const { blocks } = await api.saver.save();

        const previous = formValues.content || [];
        const maxLen = Math.max(previous.length, blocks.length);
        const diff: Array<{ index: number; previous: unknown; current: unknown }> = [];

        for (let i = 0; i < maxLen; i++) {
          if (JSON.stringify(previous[i]) !== JSON.stringify(blocks[i])) {
            diff.push({ index: i, previous: previous[i], current: blocks[i] });
          }
        }

        if (diff.length > 0) {
          console.log("Editor onChange diff:", diff);
        }

        if (JSON.stringify(blocks) === JSON.stringify(formValues.content)) return;

        setValue("content", blocks, { shouldDirty: true });
      }, 500)
    } as any);

    return () => {
      if (editorRef.current) {
        editorRef.current?.destroy();
        editorRef.current = null;
      }
    };
  }, [postLoading]);

  if (postLoading)
    return (
      <div className='flex items-center justify-center h-full'>
        <span className='animate-spin rounded-full size-6 border-2 border-[#a0a0a0] border-t-transparent' />
      </div>
    );

  return (
    <>
      <div className='w-full h-full relative'>
        <div
          className={`grid h-full grid-cols-[1fr_693px_331px_1fr] ${tab === "content" && "grid-rows-[100px_0fr_0fr_1fr]"} ${
            tab === "meta" && "grid-rows-[100px_1fr]"
          } `}
        >
          <div className='flex justify-end items-end p-2 border-dashed border-b border-[#dedede]'>
            <button
              onClick={() => navigate("/")}
              className='size-[36px] flex items-center justify-center border border-[#e0e0e0] bg-white rounded-lg cursor-pointer hover:border-[#a0a0a0] transition-colors'
            >
              <svg fill="#000000" width="28px" height="28px" viewBox="-4.5 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.469 12.594l3.625 3.313c0.438 0.406 0.313 0.719-0.281 0.719h-2.719v8.656c0 0.594-0.5 1.125-1.094 1.125h-4.719v-6.063c0-0.594-0.531-1.125-1.125-1.125h-2.969c-0.594 0-1.125 0.531-1.125 1.125v6.063h-4.719c-0.594 0-1.125-0.531-1.125-1.125v-8.656h-2.688c-0.594 0-0.719-0.313-0.281-0.719l10.594-9.625c0.438-0.406 1.188-0.406 1.656 0l2.406 2.156v-1.719c0-0.594 0.531-1.125 1.125-1.125h2.344c0.594 0 1.094 0.531 1.094 1.125v5.875z" />
              </svg>
            </button>
          </div>
          <div className='flex justify-between items-end p-2 border-dashed border-b border-x border-[#dedede] relative'>
            <div className='size-2.75 absolute -left-1.5 -bottom-1.5 flex items-center justify-center'>
              <div className='absolute h-px w-2.75 bg-[#A1A1A1]' />
              <div className='absolute h-2.75 z-10 w-px bg-[#A1A1A1]' />
            </div>
            <div className='size-2.75 absolute -right-1.5 -bottom-1.5 flex items-center justify-center'>
              <div className='absolute h-px w-2.75 bg-[#A1A1A1]' />
              <div className='absolute h-2.75 w-px bg-[#A1A1A1] z-10' />
            </div>
            <div className='bg-[#e0e0e0] font-medium flex text-xs items-center rounded-lg shrink h-9 px-px border-[#e0e0e0] border-y'>
              <button
                className={`h-full px-3 transition-all ease-linear rounded-lg cursor-pointer uppercase font-semibold ${
                  tab === "content" && "bg-white shadow ring ring-neutral-950/10"
                }`}
                onClick={() => setTab("content")}
              >
                article content
              </button>
              <button
                className={`h-full px-3 transition-all ease-linear rounded-lg cursor-pointer uppercase font-semibold ${
                  tab === "meta" && "bg-white shadow ring ring-neutral-950/10"
                }`}
                onClick={() => setTab("meta")}
              >
                meta tags
              </button>
            </div>

            <div className='flex items-center flex-row gap-2'>
              {post?.status !== "published" && (
                <Button
                  loading={action === "saving"}
                  disabled={!isDirty || !!action}
                  onClick={handleSubmit((d) => onSubmit(d, "edit"))}
                  className='uppercase'
                >
                  edit draft
                </Button>
              )}

              <Button
                loading={action === "publishing"}
                disabled={!!action || (!isDirty && formValues.status === "published")}
                onClick={handleSubmit((d) => onSubmit(d, isDirty ? "edit_and_publish" : "publish"))}
                className='uppercase'
              >
                {formValues.status === "published" || isDirty ? "edit & publish" : "publish"}
              </Button>
            </div>
          </div>
          <div className='flex items-end border-dashed border-b border-[#dedede] p-3 col-span-2'>
            {message && (
              <div className='flex flex-row items-center gap-1 h-fit w-fit text-sm font-medium'>
                <CheckCircle2Icon
                  size={16}
                  className='text-green-600'
                />
                <span>{message}</span>
              </div>
            )}
          </div>
          <div className='flex justify-center border-dashed border-b border-[#dedede]' />
          {tab === "meta" && (
            <>
              <div className='flex justify-center p-2 border-dashed border-x border-[#dedede] row-span-2'>
                <div className='bg-white w-full p-4 rounded-lg h-100'>
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-1'>
                      <span className='text-xs uppercase font-semibold'> Article slug </span>•
                      <button
                        className='text-blue-500 text-xs font-semibold uppercase'
                        onClick={() => setValue("slug", toTranslit(formValues.title?.trim() || ""), { shouldDirty: true })}
                      >
                        generate
                      </button>
                    </div>
                    <input
                      className={`unset inline-flex items-center justify-center text-sm leading-none h-8 box-border rounded-sm p-2 border outline-none max-w-112.5 w-full border-[#e0e1e2] mt-0.5 bg-[#f8f8f8]`}
                      {...register("slug")}
                      onPaste={(e) => {
                        const input = e.target as HTMLInputElement;
                        setValue("slug", input.value, { shouldDirty: true });
                      }}
                      type='text'
                    />
                  </div>
                  <div className='flex flex-col mt-8'>
                    <div className='flex items-center gap-1'>
                      <span className='text-xs uppercase font-semibold'> Meta title </span>

                      {formValues.metaTitle !== "" && (
                        <>
                          <span className='text-[#a0a0a0]'>•</span>
                          <span className='text-xs min-w-37.5 text-[#a0a0a0]'>{formValues.metaTitle?.length || 0} chars</span>
                        </>
                      )}
                    </div>
                    <input
                      className='unset inline-flex items-center justify-center text-sm leading-none h-8 box-border rounded-sm p-2 border outline-none max-w-112.5 w-full border-[#e0e1e2] mt-0.5 bg-[#f8f8f8]'
                      {...register("metaTitle")}
                      onPaste={(e) => {
                        const input = e.target as HTMLInputElement;
                        setValue("metaTitle", input.value);
                      }}
                      type='text'
                    />
                  </div>
                  <div className='flex flex-col mt-5'>
                    <div className='flex items-center gap-1'>
                      <span className='text-xs uppercase font-semibold'> Meta description </span>

                      {formValues.metaDescription !== "" && (
                        <>
                          <span className='text-[#a0a0a0]'>•</span>
                          <span className='text-xs min-w-37.5 text-[#a0a0a0]'>{formValues.metaDescription?.length || 0} chars</span>
                        </>
                      )}
                    </div>
                    <textarea
                      className='unset inline-flex items-center justify-center text-sm leading-none h-8 box-border rounded-sm p-2 border outline-none max-w-112.5 w-full border-[#e0e1e2] mt-0.5 bg-[#f8f8f8] min-h-18'
                      {...register("metaDescription")}
                      onPaste={(e) => {
                        const input = e.target as HTMLInputElement;
                        setValue("metaDescription", input.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className='flex justify-center' />
            </>
          )}
          {tab === "content" && (
            <>
              <div className='flex justify-center border-dashed border-x border-[#dedede] border-b relative'>
                <div className='size-2.75 absolute -left-px -bottom-1.5 flex items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1] z-10' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1] z-10' />
                </div>
                <div className='size-2.75 absolute -right-px -bottom-1.5 flex justify-end items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1] z-10' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1] z-10' />
                </div>
                <div className='flex flex-col w-full p-2'>
                  <textarea
                    rows={1}
                    className={`unset text-sm leading-none box-border h-auto p-4 border outline-none w-full font-bold text-[24px] text-[#21272e] bg-white border-[#e0e0e0] rounded-lg resize-none overflow-hidden placeholder:text-[#c0c0c0] `}
                    placeholder='Article title'
                    {...register("title", {
                      onChange: (e) => inferSlugFromTitle(e.target.value)
                    })}
                    onPaste={(e) => {
                      const textarea = e.target as HTMLTextAreaElement;
                      const title = textarea.value;
                      setValue("title", title, { shouldDirty: true });
                      inferSlugFromTitle(title);
                    }}
                  />
                </div>
              </div>

              <div className='flex flex-col gap-2 p-2 group row-span-2'>
                <div className='flex flex-row gap-1 items-center w-68 border-solid border-[#e0e0e0] bg-white rounded-lg border px-2 h-10'>
                  <span className='text-gray-500 text-xs uppercase ml-1'>Blog:</span>
                  <span className='text-sm text-[#21272e] capitalize'>{post?.blog || <span className='text-[#a0a0a0]'>--</span>}</span>
                </div>

                <div className='flex flex-row gap-1 items-center w-68 border-solid border-[#e0e0e0] bg-white rounded-lg border px-2 h-10'>
                  <span className='text-gray-500 text-xs uppercase ml-1'>Status:</span>
                  <span className='text-sm text-[#21272e] capitalize'>{post?.status || <span className='text-[#a0a0a0]'>--</span>}</span>
                </div>

                <div className='border-t border-dashed border-[#dedede] m-1 mb-2 w-67' />

                {/* Date picker */}
                <Controller
                  control={control}
                  name='createdAt'
                  render={({ field }) => (
                    <DatePicker
                      customInput={
                        <div className='flex flex-row gap-1 items-center w-68 border-solid border-[#e0e0e0] bg-white rounded-lg border px-2 h-10 cursor-pointer hover:border-[#a0a0a0] transition-colors'>
                          <span className='text-gray-500 text-xs uppercase ml-1'>Date:</span>

                          <span className='text-sm text-[#21272e]'>
                            {field.value ? (
                              (() => {
                                const now = new Date();
                                const date = new Date(field.value);

                                return date.toLocaleDateString(formValues.language || "en", {
                                  month: "short",
                                  day: "numeric",
                                  ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" })
                                });
                              })()
                            ) : (
                              <span className='text-[#a0a0a0]'>--</span>
                            )}
                          </span>
                        </div>
                      }
                      selected={field.value ? new Date(field.value) : null}
                      onChange={(date: Date | null) => field.onChange(date)}
                      todayButton='Today'
                      dateFormat='MMM d, yyyy'
                      dropdownMode='select'
                      calendarClassName='custom-datepicker'
                      wrapperClassName='datepicker-toggle-close'
                      popperClassName='custom-datepicker-popper'
                      popperPlacement='bottom-start'
                    />
                  )}
                />

                {/* Category Selector */}
                <div className='flex flex-row gap-1'>
                  <Selector
                    label='Category'
                    value={formValues.category || ""}
                    onChange={(value) => setValue("category", value, { shouldDirty: true })}
                    options={
                      meta?.categories.map((category) => ({
                        value: category.slug,
                        label: category.title
                      })) || []
                    }
                  />
                  <Button
                    variant='icon'
                    onClick={() => setIsCategoryModalOpen(true)}
                    className='size-10 opacity-0 group-hover:opacity-100 transition-opacity'
                  >
                    <PlusIcon
                      size={18}
                      color='#a0a0a0'
                    />
                  </Button>
                </div>

                {/* Author Selector */}
                <Selector
                  label='Author'
                  value={formValues.author || ""}
                  onChange={(value) => setValue("author", value, { shouldDirty: true })}
                  options={
                    meta?.authors.map((author) => ({
                      value: author.slug,
                      label: author.name
                    })) || []
                  }
                />

                <div className='border-t border-dashed border-[#dedede] m-1 mb-2 w-67' />

                {/* Language Selector */}

                <div className='flex flex-row gap-1'>
                  <Selector
                    label='Language'
                    value={formValues.language || ""}
                    onChange={() => {}}
                    options={availableLanguages}
                    disabled={true}
                  />

                  {post && post.availableLanguages.length > 0 && (
                    <Button
                      variant='icon'
                      onClick={() => setIsLocalizationModalOpen(true)}
                      className='size-10 opacity-0 group-hover:opacity-100 transition-opacity'
                    >
                      <PlusIcon
                        size={18}
                        color='#a0a0a0'
                      />
                    </Button>
                  )}
                </div>

                <div className='border-t border-dashed border-[#dedede] m-1 mb-2 w-67' />
                <span
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className='uppercase text-red-600 text-sm cursor-pointer mx-1 w-fit'
                >
                  delete
                </span>
              </div>

              <div className='flex justify-center' />

              <div className='flex justify-center' />
              <div className='flex flex-col border-dashed border-x border-[#dedede] border-b relative'>
                <div className='size-2.75 absolute -left-px -bottom-1.5 flex items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1]' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1]' />
                </div>
                <div className='size-2.75 absolute -right-px -bottom-1.5 flex justify-end items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1]' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1]' />
                </div>
                <div className='p-2'>
                  {formValues.previewImage ? (
                    <div
                      className='flex flex-col gap-2 w-168.75'
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <div className='relative group'>
                        <img
                          src={formValues.previewImage}
                          alt={formValues.previewImageAlt || ""}
                          className='rounded-lg w-full'
                        />
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setValue("previewImage", null);
                            setPreviewImageFile(null);
                            setValue("previewImageAlt", "");
                          }}
                          className='absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-sm cursor-pointer transition-all opacity-0 group-hover:opacity-100'
                        >
                          <X
                            size={16}
                            className='text-gray-600'
                          />
                        </button>
                      </div>
                      <input
                        {...register("previewImageAlt")}
                        type='text'
                        placeholder='Alt text'
                        className='unset inline-flex items-center justify-center text-sm leading-none h-9 box-border rounded-md px-3 border outline-none w-full text-[#21272e] border-[#e0e1e2] bg-white wrap-break-words placeholder:text-[#c0c0c0]'
                      />
                    </div>
                  ) : (
                    <label
                      htmlFor='file-upload'
                      className='border border-[#e0e0e0] cursor-pointer flex p-2 leading-4 whitespace-pre rounded-lg bg-white text-xs text-gray-700 w-full h-79.5'
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <div className='border border-dashed w-full h-full flex-col flex items-center justify-center border-[#e0e0e0] rounded-sm'>
                        <ImagePlusIcon
                          size={40}
                          color='#a0a0a0'
                        />
                        <p className='mt-6 text-[14px] uppercase font-medium text-[#a0a0a0]'>Choose article cover or drag it here</p>
                      </div>
                    </label>
                  )}
                  <input
                    type='file'
                    id='file-upload'
                    className='hidden'
                    onChange={({ target }) => {
                      if (target.files && target.files[0]) {
                        const file = target.files[0];
                        setPreviewImageFile(file);
                        setValue("previewImage", URL.createObjectURL(file), { shouldDirty: true });
                      }
                    }}
                  />
                </div>
              </div>
              <div className='flex justify-center' />
              <div className='flex justify-center' />
            </>
          )}

          {/* Editor moved outside conditional blocks to prevent destruction on tab change */}
          <div className={`flex justify-center border-dashed border-x border-[#e0e0e0]  ${tab !== "content" && "hidden"}`}>
            <div className='flex w-full'>
              <div className='flex flex-col flex-1 p-2'>
                <div
                  id='editorjs'
                  className={`w-full text-sm min-h-100 box-border rounded-smborder border border-[#e0e0e0] rounded-lg bg-white outline-none`}
                />
              </div>
            </div>
          </div>
          <div className={`flex justify-center ${tab !== "content" && "hidden"}`} />
          <div className={`flex justify-center ${tab !== "content" && "hidden"}`} />
        </div>
      </div>

      <AddCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onCategoryAdded={handleCategoryAdded}
      />

      <DeletePostModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={async () => {
          await deletePost(id!);
          navigate("/");
        }}
      />

      <CreateLocalizationModal
        isOpen={isLocalizationModalOpen}
        onClose={() => setIsLocalizationModalOpen(false)}
        parentPost={post}
      />
    </>
  );
}
